import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { apiFetch } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { aggiungiGiorniIso, oggiRoma } from '../../../utils/dateRoma';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { Campo, StatoColonnina, StatoPresa } from './componenti';
import {
  BadgeAvvio, Comando, Connettore, MOD_COLONNINE, PERCORSO_COLONNINE, SchedaColonnina, SedeOpzione,
  STATO_COMANDO, TIPO_COMANDO, Tariffa, TipoComando,
  formatDataOra, formatGiorno, messaggioErrore, whInKwh,
} from './tipi';
import { API_DOCS } from './ColonninaRicaricaSchedaPage.api';

type Parametri = Comando['parametri'];

interface Conferma {
  titolo: string;
  testo: string;
  etichetta: string;
  pericolosa?: boolean;
  azione: () => Promise<void>;
}

interface FormModifica {
  nome: string;
  sedeId: string;
  attiva: boolean;
}

interface FormTariffa {
  prezzo: string;
  validaDal: string;
}

interface VoceConfigurazione {
  key: string;
  value?: string;
  readonly: boolean;
}

const URL_GATEWAY = 'wss://ocpp.pianinvest.it';
const INTERVALLO_AGGIORNAMENTO_MS = 3000;
const RITARDO_RILETTURA_STATO_MS = 4000;
const PREZZO_MASSIMO = 9999.99;

const inElaborazione = (c: Comando) => c.stato === 'IN_ATTESA' || c.stato === 'INVIATO';

function statoTariffa(t: Tariffa, inVigore: Tariffa | null, oggi: string): { label: string; badge: string } {
  if (t.validaDal > oggi) return { label: 'Futura', badge: 'bop-badge--info' };
  if (inVigore && t.id === inVigore.id) return { label: 'In vigore', badge: 'bop-badge--success' };
  return { label: 'Superata', badge: 'bop-badge--neutral' };
}

// Descrizione leggibile dei parametri di un comando, per lo storico
function dettaglioComando(c: Comando): string {
  const p = c.parametri;
  switch (c.tipo) {
    case 'AVVIO_REMOTO': return `Presa ${p?.connettore ?? '—'} · badge ${p?.codiceBadge ?? '—'}`;
    case 'STOP_REMOTO': return `Ricarica n. ${p?.transactionId ?? '—'}`;
    case 'RESET': return p?.tipo === 'Hard' ? 'Completo' : 'Normale';
    case 'SBLOCCO': return `Presa ${p?.connettore ?? '—'}`;
    case 'DISPONIBILITA': return p?.operativa ? 'Rimessa in servizio' : 'Messa fuori servizio';
    default: return '—';
  }
}

// Messaggio da mostrare nello storico: quello del gateway, o la risposta della colonnina se ha rifiutato
function messaggioComando(c: Comando): string {
  if (c.messaggio) return c.messaggio;
  const status = c.risposta?.status;
  if (c.stato === 'RIFIUTATO' && typeof status === 'string') return `La colonnina ha risposto: ${status}`;
  return '—';
}

function vociConfigurazione(c: Comando): VoceConfigurazione[] {
  const voci = c.risposta?.configurationKey;
  return Array.isArray(voci) ? (voci as VoceConfigurazione[]) : [];
}

export default function ColonninaRicaricaSchedaPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermesso();

  const [scheda, setScheda] = useState<SchedaColonnina | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [erroreAgg, setErroreAgg] = useState('');
  const [comandi, setComandi] = useState<Comando[]>([]);
  const [erroreComandi, setErroreComandi] = useState('');
  const [tariffe, setTariffe] = useState<Tariffa[]>([]);
  const [erroreTariffe, setErroreTariffe] = useState('');

  const [occupato, setOccupato] = useState(false);
  const [conferma, setConferma] = useState<Conferma | null>(null);
  const [avvio, setAvvio] = useState<Connettore | null>(null);
  const [badgeAvvio, setBadgeAvvio] = useState<BadgeAvvio[] | null>(null);
  const [erroreBadge, setErroreBadge] = useState('');
  const [badgeScelto, setBadgeScelto] = useState('');
  const [password, setPassword] = useState<{ valore: string; rigenerata: boolean } | null>(null);
  const [avvisoPassword, setAvvisoPassword] = useState('');
  const [configurazione, setConfigurazione] = useState<VoceConfigurazione[] | null>(null);

  // Modifica
  const [formModifica, setFormModifica] = useState<FormModifica | null>(null);
  const [snapshotModifica, setSnapshotModifica] = useState('');
  const [erroreModifica, setErroreModifica] = useState('');
  const [sedi, setSedi] = useState<SedeOpzione[]>([]);

  // Nuova tariffa
  const [formTariffa, setFormTariffa] = useState<FormTariffa | null>(null);
  const [snapshotTariffa, setSnapshotTariffa] = useState('');
  const [erroreFormTariffa, setErroreFormTariffa] = useState('');

  const colonnina = scheda?.colonnina ?? null;
  const gatewayOk = scheda?.gateway.raggiungibile ?? false;
  const societaId = colonnina?.societaId;

  const canEdit = can(MOD_COLONNINE, 'edit', societaId);
  const canDelete = can(MOD_COLONNINE, 'delete', societaId);

  const isDirtyModifica = formModifica !== null && JSON.stringify(formModifica) !== snapshotModifica;
  useDirtyForm(isDirtyModifica, `Modifica colonnina ${colonnina?.nome ?? ''}`.trim());
  const isDirtyTariffa = formTariffa !== null && JSON.stringify(formTariffa) !== snapshotTariffa;
  useDirtyForm(isDirtyTariffa, 'Nuova tariffa');

  useBloccoRecordContesto({
    recordSocietaId: colonnina?.societaId,
    label: colonnina ? `Colonnina ${colonnina.nome}` : 'Colonnina',
  });

  // Comandi che all'ultima lettura non erano ancora conclusi: serve a capire quando uno si conclude
  const inElaborazionePrima = useRef<Set<number>>(new Set());
  const timerRilettura = useRef<number | null>(null);

  // ── Caricamenti ────────────────────────────────────────────────────────────
  const caricaScheda = useCallback(async (silenzioso = false) => {
    if (!silenzioso) {
      setLoading(true);
      setErrore('');
    }
    try {
      // @api GET /api/ricarica/colonnine/:id -> {success:true,data:{gateway:{raggiungibile,ultimoContattoAt},colonnina:{id,societaId,societaNome,nome,codiceOcpp,sede,marca,modello,seriale,firmware,collegata,ultimoContattoAt,stato,codiceErrore,attiva,passwordImpostata,connettori}}}
      const res = await apiFetch<{ success: boolean; data: SchedaColonnina }>(`/api/ricarica/colonnine/${id}`);
      setScheda(res.data);
      setErroreAgg('');
    } catch (err) {
      const msg = messaggioErrore(err, 'Errore nel caricamento della colonnina');
      if (silenzioso) setErroreAgg(`Aggiornamento dello stato non riuscito: ${msg}`);
      else setErrore(msg);
    } finally {
      if (!silenzioso) setLoading(false);
    }
  }, [id]);

  const caricaComandi = useCallback(async () => {
    try {
      // @api GET /api/ricarica/colonnine/:id/comandi -> {success:true,data:[{id,tipo,parametri,stato,risposta,messaggio,richiestoDa,createdAt,esitoAt}]}
      const res = await apiFetch<{ success: boolean; data: Comando[] }>(`/api/ricarica/colonnine/${id}/comandi`);
      const adesso = new Set(res.data.filter(inElaborazione).map((c) => c.id));
      const concluso = Array.from(inElaborazionePrima.current).some((x) => !adesso.has(x));
      inElaborazionePrima.current = adesso;
      setComandi(res.data);
      setErroreComandi('');
      if (concluso) {
        // L'esito di un comando di solito cambia lo stato della colonnina: lo si rilegge subito e
        // ancora poco dopo, perché la notifica di stato può arrivare un attimo più tardi
        caricaScheda(true);
        if (timerRilettura.current !== null) window.clearTimeout(timerRilettura.current);
        timerRilettura.current = window.setTimeout(() => caricaScheda(true), RITARDO_RILETTURA_STATO_MS);
      }
    } catch (err) {
      setErroreComandi(messaggioErrore(err, 'Errore nel caricamento dei comandi'));
    }
  }, [id, caricaScheda]);

  const caricaTariffe = useCallback(async () => {
    if (!societaId) return;
    try {
      // @api GET /api/ricarica/tariffe?societaId -> {success:true,data:[{id,prezzoKwh,validaDal,creatoDa}]}
      const res = await apiFetch<{ success: boolean; data: Tariffa[] }>(`/api/ricarica/tariffe?societaId=${societaId}`);
      setTariffe(res.data);
      setErroreTariffe('');
    } catch (err) {
      setErroreTariffe(messaggioErrore(err, 'Errore nel caricamento delle tariffe'));
    }
  }, [societaId]);

  useEffect(() => {
    inElaborazionePrima.current = new Set();
    setComandi([]);
    caricaScheda();
    caricaComandi();
    return () => {
      if (timerRilettura.current !== null) window.clearTimeout(timerRilettura.current);
    };
  }, [caricaScheda, caricaComandi]);

  useEffect(() => { caricaTariffe(); }, [caricaTariffe]);

  // Finché un comando è in attesa o inviato si rilegge lo storico ogni pochi secondi
  const haComandiInElaborazione = comandi.some(inElaborazione);
  useEffect(() => {
    if (!haComandiInElaborazione) return;
    const timer = window.setInterval(() => { caricaComandi(); }, INTERVALLO_AGGIORNAMENTO_MS);
    return () => window.clearInterval(timer);
  }, [haComandiInElaborazione, caricaComandi]);

  // ── Comandi ────────────────────────────────────────────────────────────────
  const invia = async (tipo: TipoComando, parametri: Parametri) => {
    setOccupato(true);
    try {
      // @api POST /api/ricarica/colonnine/:id/comandi {tipo,parametri} -> {success:true,data:{id}}
      await apiFetch<{ success: boolean; data: { id: number } }>(`/api/ricarica/colonnine/${id}/comandi`, {
        method: 'POST',
        body: JSON.stringify({ tipo, parametri }),
      });
      showToast("Comando inviato: l'esito compare nello storico", 'success');
      setConferma(null);
      setAvvio(null);
      await caricaComandi();
    } catch (err) {
      showToast(messaggioErrore(err, "Errore nell'invio del comando"), 'error');
      setConferma(null);
      setAvvio(null);
      caricaScheda(true);
    } finally {
      setOccupato(false);
    }
  };

  const apriAvvio = async (presa: Connettore) => {
    setBadgeScelto('');
    setBadgeAvvio(null);
    setErroreBadge('');
    setAvvio(presa);
    try {
      // @api GET /api/ricarica/colonnine/:id/badge-avvio -> {success:true,data:[{codice,dipendente}]}
      const res = await apiFetch<{ success: boolean; data: BadgeAvvio[] }>(`/api/ricarica/colonnine/${id}/badge-avvio`);
      setBadgeAvvio(res.data);
    } catch (err) {
      setErroreBadge(messaggioErrore(err, 'Errore nel caricamento dei badge'));
      setBadgeAvvio([]);
    }
  };

  const chiediFermaRicarica = (presa: Connettore) => {
    const sessione = presa.sessioneInCorso;
    if (!sessione) return;
    setConferma({
      titolo: 'Fermare la ricarica?',
      testo: `La ricarica sulla presa ${presa.numero} viene chiusa e conteggiata con l'energia erogata fin qui.`,
      etichetta: 'Conferma',
      azione: () => invia('STOP_REMOTO', { transactionId: sessione.transactionId }),
    });
  };

  const chiediComando = (tipo: TipoComando, parametri: Parametri, titolo: string, testo: string) => {
    setConferma({ titolo, testo, etichetta: 'Conferma', azione: () => invia(tipo, parametri) });
  };

  const eseguiConferma = async () => {
    if (!conferma) return;
    await conferma.azione();
  };

  // ── Password ───────────────────────────────────────────────────────────────
  const generaPassword = async () => {
    if (!colonnina) return;
    const rigenerata = colonnina.passwordImpostata;
    setOccupato(true);
    try {
      // @api POST /api/ricarica/colonnine/:id/password -> {success:true,data:{password}}
      const res = await apiFetch<{ success: boolean; data: { password: string } }>(
        `/api/ricarica/colonnine/${id}/password`,
        { method: 'POST' },
      );
      setAvvisoPassword('');
      setConferma(null);
      setPassword({ valore: res.data.password, rigenerata });
      caricaScheda(true);
    } catch (err) {
      const msg = messaggioErrore(err, 'Errore nella generazione della password');
      setConferma(null);
      // 409 "Tariffa mancante": resta visibile in pagina finché non si inserisce la tariffa
      if ((err as { status?: number }).status === 409) setAvvisoPassword(msg);
      else showToast(msg, 'error');
    } finally {
      setOccupato(false);
    }
  };

  const chiediPassword = () => {
    if (!colonnina) return;
    if (!colonnina.passwordImpostata) {
      generaPassword();
      return;
    }
    setConferma({
      titolo: 'Rigenerare la password?',
      testo: 'La password attuale smette di funzionare: la colonnina viene scollegata e si ricollega solo con la nuova password.',
      etichetta: 'Rigenera password',
      azione: generaPassword,
    });
  };

  // ── Modifica ───────────────────────────────────────────────────────────────
  const apriModifica = async () => {
    if (!colonnina) return;
    const iniziale: FormModifica = {
      nome: colonnina.nome,
      sedeId: colonnina.sede ? String(colonnina.sede.id) : '',
      attiva: colonnina.attiva,
    };
    setErroreModifica('');
    setFormModifica(iniziale);
    setSnapshotModifica(JSON.stringify(iniziale));
    try {
      // @api GET /api/ricarica/sedi?societaId -> {success:true,data:[{id,nome}]}
      const res = await apiFetch<{ success: boolean; data: SedeOpzione[] }>(
        `/api/ricarica/sedi?societaId=${colonnina.societaId}`,
      );
      setSedi(res.data);
    } catch (err) {
      setSedi([]);
      setErroreModifica(messaggioErrore(err, 'Errore nel caricamento delle sedi'));
    }
  };

  const salvaModifica = async () => {
    if (!formModifica) return;
    const nome = formModifica.nome.trim();
    if (!nome) { setErroreModifica('Il nome è obbligatorio'); return; }
    setOccupato(true);
    setErroreModifica('');
    try {
      // @api PUT /api/ricarica/colonnine/:id {nome,sedeId,attiva} -> {success:true}
      await apiFetch(`/api/ricarica/colonnine/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nome,
          sedeId: formModifica.sedeId ? parseInt(formModifica.sedeId, 10) : null,
          attiva: formModifica.attiva,
        }),
      });
      setSnapshotModifica(JSON.stringify(formModifica));
      setFormModifica(null);
      showToast('Colonnina salvata', 'success');
      caricaScheda(true);
    } catch (err) {
      setErroreModifica(messaggioErrore(err, 'Errore nel salvataggio della colonnina'));
    } finally {
      setOccupato(false);
    }
  };

  // ── Tariffe ────────────────────────────────────────────────────────────────
  const oggi = oggiRoma();
  const tariffaInVigore = tariffe
    .filter((t) => t.validaDal <= oggi)
    .sort((a, b) => b.validaDal.localeCompare(a.validaDal))[0] ?? null;

  const apriTariffa = () => {
    // Senza una tariffa in vigore (serve per generare le password) si propone oggi, altrimenti domani
    const iniziale: FormTariffa = {
      prezzo: '',
      validaDal: tariffaInVigore ? aggiungiGiorniIso(oggi, 1) : oggi,
    };
    setErroreFormTariffa('');
    setFormTariffa(iniziale);
    setSnapshotTariffa(JSON.stringify(iniziale));
  };

  const salvaTariffa = async () => {
    if (!formTariffa || !colonnina) return;
    const prezzo = Number(formTariffa.prezzo);
    if (!formTariffa.prezzo || !(prezzo > 0) || prezzo > PREZZO_MASSIMO) {
      setErroreFormTariffa('Il prezzo deve essere maggiore di zero e al massimo 9999,99');
      return;
    }
    if (Math.round(prezzo * 100) / 100 !== prezzo) {
      setErroreFormTariffa('Il prezzo può avere al massimo 2 decimali');
      return;
    }
    if (!formTariffa.validaDal) { setErroreFormTariffa('La data di inizio è obbligatoria'); return; }
    if (formTariffa.validaDal < oggi) { setErroreFormTariffa('La validità non può iniziare nel passato'); return; }
    setOccupato(true);
    setErroreFormTariffa('');
    try {
      // @api POST /api/ricarica/tariffe {societaId,prezzoKwh,validaDal} -> {success:true,data:{id}}
      await apiFetch<{ success: boolean; data: { id: number } }>('/api/ricarica/tariffe', {
        method: 'POST',
        body: JSON.stringify({ societaId: colonnina.societaId, prezzoKwh: prezzo, validaDal: formTariffa.validaDal }),
      });
      setSnapshotTariffa(JSON.stringify(formTariffa));
      setFormTariffa(null);
      setAvvisoPassword('');
      showToast('Tariffa salvata', 'success');
      caricaTariffe();
    } catch (err) {
      setErroreFormTariffa(messaggioErrore(err, 'Errore nel salvataggio della tariffa'));
    } finally {
      setOccupato(false);
    }
  };

  const eliminaTariffa = async (t: Tariffa) => {
    setOccupato(true);
    try {
      // @api DELETE /api/ricarica/tariffe/:id -> {success:true}
      await apiFetch(`/api/ricarica/tariffe/${t.id}`, { method: 'DELETE' });
      showToast('Tariffa eliminata', 'success');
      caricaTariffe();
    } catch (err) {
      showToast(messaggioErrore(err, "Errore nell'eliminazione della tariffa"), 'error');
    } finally {
      setConferma(null);
      setOccupato(false);
    }
  };

  const chiediEliminaTariffa = (t: Tariffa) => {
    setConferma({
      titolo: 'Eliminare la tariffa?',
      testo: `La tariffa di ${formatCurrency(t.prezzoKwh)} €/kWh valida dal ${formatGiorno(t.validaDal)} viene eliminata. Non si può eliminare se una ricarica la usa.`,
      etichetta: 'Elimina',
      pericolosa: true,
      azione: () => eliminaTariffa(t),
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="bop-ricarica__stack">
          <BopBackLink to={PERCORSO_COLONNINE} label="Torna alle colonnine" />
          <div className="inline-loading">Caricamento…</div>
        </div>
      </Layout>
    );
  }

  if (errore || !scheda || !colonnina) {
    return (
      <Layout>
        <div className="bop-ricarica__stack">
          <BopBackLink to={PERCORSO_COLONNINE} label="Torna alle colonnine" />
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">{errore || 'Colonnina non trovata'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const fuoriServizio = colonnina.stato === 'Unavailable';
  const comandiAbilitati = canEdit && colonnina.attiva;

  const notaComandi = !canEdit
    ? 'Non hai il permesso di inviare comandi alla colonnina.'
    : !colonnina.attiva
      ? 'I comandi sono disponibili solo per le colonnine attive.'
      : gatewayOk
        ? "I comandi passano dal gateway: l'esito compare qui sotto entro pochi secondi."
        : 'Il gateway non risponde: i comandi restano in attesa e scadono dopo 2 minuti.';

  // La sede attuale resta fra le opzioni anche se nel frattempo è stata disattivata
  const opzioniSede = colonnina.sede && !sedi.some((s) => s.id === colonnina.sede?.id)
    ? [...sedi, colonnina.sede]
    : sedi;

  return (
    <Layout>
      <div className="bop-ricarica__stack">
        <BopBackLink to={PERCORSO_COLONNINE} label="Torna alle colonnine" />

        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">{colonnina.nome}</h2>
            <p className="bop-page-subtitle">{colonnina.societaNome} · {colonnina.sede?.nome ?? 'Nessuna sede'}</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-secondary" onClick={apriModifica}>Modifica</button>
              <button className="bop-btn bop-btn-secondary" onClick={chiediPassword} disabled={occupato}>
                {colonnina.passwordImpostata ? 'Rigenera password' : 'Genera password'}
              </button>
            </div>
          )}
        </div>

        {!gatewayOk && (
          <div className="bop-alert bop-alert--danger bop-mb-0">
            Gateway non raggiungibile: lo stato qui sotto è l'ultimo noto e i comandi scadono senza arrivare alla colonnina.
          </div>
        )}
        {!colonnina.attiva && (
          <div className="bop-alert bop-alert--warning bop-mb-0">
            Colonnina disattivata: non è nella configurazione del gateway e non accetta comandi. Si riattiva da Modifica.
          </div>
        )}
        {avvisoPassword && (
          <div className="bop-alert bop-alert--warning bop-mb-0">{avvisoPassword}</div>
        )}
        {erroreAgg && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreAgg}</div>}

        <div className="bop-card">
          <div className="bop-card__header"><h3 className="bop-card__title">Collegamento</h3></div>
          <div className="bop-card__body">
            <div className="bop-ricarica__campi">
              <Campo label="Stato"><StatoColonnina colonnina={colonnina} gatewayOk={gatewayOk} /></Campo>
              <Campo label="Ultimo contatto">{formatDataOra(colonnina.ultimoContattoAt)}</Campo>
              <Campo label="Indirizzo di collegamento">
                <span className="bop-text-mono">{URL_GATEWAY}/{colonnina.codiceOcpp}</span>
              </Campo>
              <Campo label="Marca e modello">
                {colonnina.marca ? `${colonnina.marca} ${colonnina.modello ?? ''}` : 'Non ancora collegata'}
              </Campo>
              <Campo label="Seriale">{colonnina.seriale ?? '—'}</Campo>
              <Campo label="Firmware">{colonnina.firmware ?? '—'}</Campo>
              <Campo label="Disponibilità">
                {colonnina.stato === null ? '—' : fuoriServizio ? 'Fuori servizio' : 'In servizio'}
              </Campo>
              {colonnina.codiceErrore && <Campo label="Errore">{colonnina.codiceErrore}</Campo>}
            </div>
          </div>
        </div>

        <div className="bop-card">
          <div className="bop-card__header"><h3 className="bop-card__title">Prese</h3></div>
          {colonnina.connettori.length === 0 ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Le prese compaiono quando la colonnina si collega la prima volta</p>
            </div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Presa</th>
                  <th>Stato</th>
                  <th>Ricarica in corso</th>
                  <th>Errore</th>
                  {canEdit && <th className="bop-table__th--actions">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {colonnina.connettori.map((p) => (
                  <tr key={p.numero}>
                    <td>Presa {p.numero}</td>
                    <td><StatoPresa presa={p} gatewayOk={gatewayOk} /></td>
                    <td>
                      {p.sessioneInCorso ? (
                        <>
                          {p.sessioneInCorso.dipendente ?? 'Badge sconosciuto'}
                          <div className="bop-ricarica__secondaria">
                            dalle {formatDataOra(p.sessioneInCorso.inizioAt)} ·{' '}
                            {formatNumber(whInKwh(p.sessioneInCorso.energiaWh))} kWh
                            {gatewayOk ? '' : ' · ultimo dato noto'}
                          </div>
                        </>
                      ) : <span className="bop-text-muted">—</span>}
                    </td>
                    <td className="bop-text-muted">{p.codiceErrore ?? '—'}</td>
                    {canEdit && (
                      <td className="bop-table__td--actions">
                        <span className="bop-ricarica__comandi bop-ricarica__comandi--destra">
                          {(p.stato === 'Available' || p.stato === 'Preparing') && (
                            <button className="bop-btn bop-btn-secondary bop-btn-sm"
                              disabled={!gatewayOk || !colonnina.attiva || occupato}
                              onClick={() => apriAvvio(p)}>
                              Avvia ricarica
                            </button>
                          )}
                          {p.sessioneInCorso && (
                            <button className="bop-btn bop-btn-secondary bop-btn-sm"
                              disabled={!gatewayOk || !colonnina.attiva || occupato}
                              onClick={() => chiediFermaRicarica(p)}>
                              Ferma ricarica
                            </button>
                          )}
                          <button className="bop-btn bop-btn-ghost bop-btn-sm" disabled={!colonnina.attiva || occupato}
                            onClick={() => invia('SBLOCCO', { connettore: p.numero })}>
                            Sblocca
                          </button>
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bop-card">
          <div className="bop-card__header"><h3 className="bop-card__title">Comandi</h3></div>
          <div className="bop-card__body bop-ricarica__form">
            {comandiAbilitati && (
              <div className="bop-ricarica__comandi">
                <button className="bop-btn bop-btn-secondary" disabled={occupato}
                  onClick={() => invia('RICHIESTA_STATO', null)}>
                  Aggiorna stato
                </button>
                <button className="bop-btn bop-btn-secondary" disabled={occupato}
                  onClick={() => invia('LEGGI_CONFIGURAZIONE', null)}>
                  Leggi configurazione
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={() => chiediComando(
                  'RESET', { tipo: 'Soft' }, 'Riavviare la colonnina?',
                  'Le ricariche in corso vengono chiuse prima del riavvio.',
                )}>
                  Riavvia
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={() => chiediComando(
                  'RESET', { tipo: 'Hard' }, 'Riavvio completo?',
                  'La colonnina si spegne e si riaccende subito, anche con ricariche in corso. Usalo solo se il riavvio normale non risponde.',
                )}>
                  Riavvio completo
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={() => chiediComando(
                  'DISPONIBILITA', { connettore: 0, operativa: fuoriServizio },
                  fuoriServizio ? 'Rimettere in servizio?' : 'Mettere fuori servizio?',
                  fuoriServizio
                    ? 'La colonnina torna disponibile per le ricariche.'
                    : 'La colonnina non accetta nuove ricariche finché non la rimetti in servizio.',
                )}>
                  {fuoriServizio ? 'Rimetti in servizio' : 'Metti fuori servizio'}
                </button>
              </div>
            )}
            <p className="bop-ricarica__nota">{notaComandi}</p>
            {erroreComandi && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreComandi}</div>}
          </div>
          <table className="bop-table">
            <thead>
              <tr>
                <th>Richiesto il</th>
                <th>Comando</th>
                <th>Dettaglio</th>
                <th>Richiesto da</th>
                <th>Esito</th>
                <th>Messaggio</th>
              </tr>
            </thead>
            <tbody>
              {comandi.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="bop-empty-state">
                      <p className="bop-empty-state__text">Nessun comando inviato a questa colonnina</p>
                    </div>
                  </td>
                </tr>
              ) : comandi.map((c) => (
                <tr key={c.id}>
                  <td>{formatDataOra(c.createdAt)}</td>
                  <td>{TIPO_COMANDO[c.tipo] ?? c.tipo}</td>
                  <td>
                    {c.tipo === 'LEGGI_CONFIGURAZIONE' && c.risposta ? (
                      <button className="bop-btn bop-btn-ghost bop-btn-sm"
                        onClick={() => setConfigurazione(vociConfigurazione(c))}>
                        Vedi configurazione
                      </button>
                    ) : dettaglioComando(c)}
                  </td>
                  <td>{c.richiestoDa}</td>
                  <td>
                    <span className={`bop-badge ${STATO_COMANDO[c.stato].badge}`}>{STATO_COMANDO[c.stato].label}</span>
                  </td>
                  <td className="bop-text-muted">{messaggioComando(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Tariffe di {colonnina.societaNome}</h3>
            {canEdit && (
              <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={apriTariffa}>+ Nuova tariffa</button>
            )}
          </div>
          <div className="bop-card__body bop-ricarica__form">
            <p className="bop-ricarica__nota">
              Valgono per tutte le colonnine della società. Ogni ricarica usa la tariffa in vigore il giorno in cui inizia.
            </p>
            {erroreTariffe && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreTariffe}</div>}
          </div>
          <table className="bop-table">
            <thead>
              <tr>
                <th>Valida dal</th>
                <th className="bop-text-right">Prezzo</th>
                <th>Inserita da</th>
                <th>Stato</th>
                {canDelete && <th className="bop-table__th--actions">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {tariffe.length === 0 ? (
                <tr>
                  <td colSpan={canDelete ? 5 : 4}>
                    <div className="bop-empty-state">
                      <p className="bop-empty-state__text">
                        Nessuna tariffa: senza una tariffa in vigore non si può generare la password delle colonnine
                      </p>
                    </div>
                  </td>
                </tr>
              ) : tariffe.map((t) => {
                const s = statoTariffa(t, tariffaInVigore, oggi);
                return (
                  <tr key={t.id}>
                    <td>{formatGiorno(t.validaDal)}</td>
                    <td className="bop-text-right">{formatCurrency(t.prezzoKwh)} €/kWh</td>
                    <td>{t.creatoDa}</td>
                    <td><span className={`bop-badge ${s.badge}`}>{s.label}</span></td>
                    {canDelete && (
                      <td className="bop-table__td--actions">
                        <BopRowActions actions={[
                          {
                            icon: <RowIcon.Trash />, label: 'Elimina', variant: 'danger',
                            onClick: () => chiediEliminaTariffa(t),
                          },
                        ]} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {conferma && (
          <div className="bop-modal-overlay" onClick={() => !occupato && setConferma(null)}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">{conferma.titolo}</h2>
                <button className="bop-modal__close" onClick={() => setConferma(null)} disabled={occupato}
                  aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body">
                <p className="bop-ricarica__nota">{conferma.testo}</p>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setConferma(null)} disabled={occupato}>
                  Annulla
                </button>
                <button className={`bop-btn ${conferma.pericolosa ? 'bop-btn-danger' : 'bop-btn-primary'}`}
                  onClick={eseguiConferma} disabled={occupato}>
                  {occupato ? 'Invio…' : conferma.etichetta}
                </button>
              </div>
            </div>
          </div>
        )}

        {avvio && (
          <div className="bop-modal-overlay" onClick={() => !occupato && setAvvio(null)}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Avvia ricarica sulla presa {avvio.numero}</h2>
                <button className="bop-modal__close" onClick={() => setAvvio(null)} disabled={occupato}
                  aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                {erroreBadge && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreBadge}</div>}
                {badgeAvvio === null ? (
                  <p className="bop-ricarica__nota">Caricamento dei badge…</p>
                ) : badgeAvvio.length === 0 ? (
                  !erroreBadge && (
                    <p className="bop-ricarica__nota">
                      Nessun badge valido oggi fra i dipendenti di {colonnina.societaNome}.
                    </p>
                  )
                ) : (
                  <div className="bop-field">
                    <label className="bop-field__label" htmlFor="avvio-badge">
                      Badge <span className="bop-field__required">*</span>
                    </label>
                    <select id="avvio-badge" className="bop-field__select" value={badgeScelto}
                      onChange={(e) => setBadgeScelto(e.target.value)}>
                      <option value="">Scegli…</option>
                      {badgeAvvio.map((b) => (
                        <option key={b.codice} value={b.codice}>{b.dipendente} · {b.codice}</option>
                      ))}
                    </select>
                    <p className="bop-field__hint">
                      La ricarica viene intestata al titolare del badge: si possono scegliere solo dipendenti della
                      società della colonnina.
                    </p>
                  </div>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setAvvio(null)} disabled={occupato}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" disabled={!badgeScelto || occupato}
                  onClick={() => invia('AVVIO_REMOTO', { connettore: avvio.numero, codiceBadge: badgeScelto })}>
                  {occupato ? 'Invio…' : 'Avvia'}
                </button>
              </div>
            </div>
          </div>
        )}

        {password && (
          <div className="bop-modal-overlay">
            <div className="bop-modal">
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Password della colonnina</h2>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                <div className="bop-alert bop-alert--warning bop-mb-0">
                  Copiala adesso nella configurazione della colonnina: dopo la chiusura non sarà più visibile.
                  {password.rigenerata
                    ? ' Da questo momento la password precedente non funziona più: la colonnina viene scollegata e si ricollega solo con questa.'
                    : ''}
                </div>
                <div className="bop-ricarica__campi">
                  <Campo label="Indirizzo">
                    <span className="bop-text-mono">{URL_GATEWAY}/{colonnina.codiceOcpp}</span>
                  </Campo>
                  <Campo label="Utente"><span className="bop-text-mono">{colonnina.codiceOcpp}</span></Campo>
                </div>
                <div className="bop-ricarica__password bop-text-mono">{password.valore}</div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-primary" onClick={() => setPassword(null)}>
                  Ho copiato la password
                </button>
              </div>
            </div>
          </div>
        )}

        {configurazione && (
          <div className="bop-modal-overlay" onClick={() => setConfigurazione(null)}>
            <div className="bop-modal bop-modal--lg" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Configurazione letta</h2>
                <button className="bop-modal__close" onClick={() => setConfigurazione(null)} aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body">
                {configurazione.length === 0 ? (
                  <div className="bop-empty-state">
                    <p className="bop-empty-state__text">La colonnina non ha restituito nessun parametro</p>
                  </div>
                ) : (
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Parametro</th>
                        <th>Valore</th>
                        <th>Modificabile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configurazione.map((v) => (
                        <tr key={v.key}>
                          <td className="bop-text-mono">{v.key}</td>
                          <td className="bop-text-mono">{v.value ?? '—'}</td>
                          <td>{v.readonly ? 'No' : 'Sì'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setConfigurazione(null)}>Chiudi</button>
              </div>
            </div>
          </div>
        )}

        {formModifica && (
          <div className="bop-modal-overlay" onClick={() => !occupato && setFormModifica(null)}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Modifica colonnina</h2>
                <button className="bop-modal__close" onClick={() => setFormModifica(null)} disabled={occupato}
                  aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                {erroreModifica && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreModifica}</div>}
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="mod-nome">
                    Nome <span className="bop-field__required">*</span>
                  </label>
                  <input id="mod-nome" className="bop-field__input" value={formModifica.nome}
                    onChange={(e) => setFormModifica({ ...formModifica, nome: e.target.value })} />
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="mod-sede">Sede</label>
                  <select id="mod-sede" className="bop-field__select" value={formModifica.sedeId}
                    onChange={(e) => setFormModifica({ ...formModifica, sedeId: e.target.value })}>
                    <option value="">Nessuna sede</option>
                    {opzioniSede.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
                  </select>
                </div>
                <label className="bop-flex">
                  <input type="checkbox" checked={formModifica.attiva}
                    onChange={(e) => setFormModifica({ ...formModifica, attiva: e.target.checked })} />
                  Colonnina attiva
                </label>
                <p className="bop-ricarica__nota">
                  Il codice OCPP non si modifica. Una colonnina disattivata esce dalla configurazione del gateway: viene
                  scollegata e non accetta né ricariche né comandi.
                </p>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setFormModifica(null)} disabled={occupato}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={salvaModifica} disabled={occupato}>
                  {occupato ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {formTariffa && (
          <div className="bop-modal-overlay" onClick={() => !occupato && setFormTariffa(null)}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Nuova tariffa</h2>
                <button className="bop-modal__close" onClick={() => setFormTariffa(null)} disabled={occupato}
                  aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                {erroreFormTariffa && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreFormTariffa}</div>}
                <div className="bop-form-row">
                  <div className="bop-field">
                    <label className="bop-field__label" htmlFor="tar-prezzo">
                      Prezzo €/kWh <span className="bop-field__required">*</span>
                    </label>
                    <input id="tar-prezzo" type="number" min="0" step="0.01" className="bop-field__input"
                      value={formTariffa.prezzo}
                      onChange={(e) => setFormTariffa({ ...formTariffa, prezzo: e.target.value })} />
                  </div>
                  <div className="bop-field">
                    <label className="bop-field__label" htmlFor="tar-dal">
                      Valida dal <span className="bop-field__required">*</span>
                    </label>
                    <input id="tar-dal" type="date" min={oggi} className="bop-field__input"
                      value={formTariffa.validaDal}
                      onChange={(e) => setFormTariffa({ ...formTariffa, validaDal: e.target.value })} />
                  </div>
                </div>
                <p className="bop-ricarica__nota">
                  Le ricariche già fatte non cambiano: ognuna tiene la tariffa in vigore il giorno in cui è iniziata.
                </p>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setFormTariffa(null)} disabled={occupato}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={salvaTariffa} disabled={occupato}>
                  {occupato ? 'Salvataggio…' : 'Salva tariffa'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
